// Your data (M7 plan D8, D9): download everything at any time; close the account (owner).
import { useEffect, useState } from 'react';
import { useOrgData } from './accountant/useOrgData.js';
import type { Api, Organisation } from './api.js';

interface Export {
  id: string;
  reason: 'request' | 'closure';
  status: 'running' | 'ready' | 'failed';
  size_bytes: string | null;
  requested_at: string;
}

export function DataPanel({ api, org }: { api: Api; org: Organisation }) {
  const exports = useOrgData<{ exports: Export[] }>(api, org.id, '/export');
  const closure = useOrgData<{ closing: boolean; closesAt: string | null }>(
    api,
    org.id,
    '/closure',
  );
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const running = (exports.data?.exports ?? []).some((e) => e.status === 'running');

  // An export runs in the background: check back until it is ready.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void exports.reload(), 5000);
    return () => clearInterval(t);
  }, [running, exports.reload]);

  async function act(path: string, body?: unknown) {
    setError('');
    try {
      await api.send(org.id, 'POST', path, body ?? {});
      await exports.reload();
      await closure.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    }
  }
  async function download(id: string) {
    const tab = window.open('', '_blank');
    try {
      const { url } = await api.get<{ url: string }>(org.id, `/export/${id}/file`);
      if (tab) tab.location.href = url;
    } catch {
      tab?.close();
    }
  }

  return (
    <section className="stack bank-card">
      <h2>Your data</h2>
      <p className="muted">
        Download everything: every year's reports for your accountant, every transaction as a
        spreadsheet, and your receipts and statements.
      </p>
      <button type="button" disabled={running} onClick={() => act('/export')}>
        {running ? 'Preparing your download' : 'Download everything'}
      </button>
      <table className="table">
        <tbody>
          {(exports.data?.exports ?? []).map((e) => (
            <tr key={e.id}>
              <td>{new Date(e.requested_at).toLocaleString('en-AU')}</td>
              <td>{e.reason === 'closure' ? 'Made when closing' : ''}</td>
              <td className="num">
                {e.size_bytes ? `${(Number(e.size_bytes) / 1_048_576).toFixed(1)} MB` : ''}
              </td>
              <td>
                {e.status === 'ready' ? (
                  <button type="button" className="link" onClick={() => download(e.id)}>
                    Download
                  </button>
                ) : e.status === 'running' ? (
                  'Preparing'
                ) : (
                  'Failed: try again'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {org.role === 'owner' && closure.data && (
        <div className="stack">
          <h2>Close this account</h2>
          {closure.data.closing ? (
            <>
              <p className="error">
                This account closes on{' '}
                {closure.data.closesAt
                  ? new Date(closure.data.closesAt).toLocaleDateString('en-AU')
                  : ''}
                . Until then it is read-only and you can download everything. Then all of it is
                deleted.
              </p>
              <button type="button" className="link" onClick={() => act('/closure/cancel')}>
                Keep my account
              </button>
            </>
          ) : (
            <>
              <p className="muted">
                Foderis makes a full download first, cancels the subscription, and keeps your books
                read-only for 90 days so you can change your mind. Then everything is deleted for
                good. Keeping records is your obligation (five years for tax, seven for companies):
                keep the download.
              </p>
              <label>
                Type {org.name} to confirm
                <input value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </label>
              <button
                type="button"
                className="link"
                disabled={confirm !== org.name}
                onClick={() => act('/closure', { confirmName: confirm })}
              >
                Close this account
              </button>
            </>
          )}
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
